import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FaArrowLeft,
  FaExternalLinkAlt,
  FaMoneyBillWave,
  FaRobot,
  FaSave,
  FaTrash,
  FaUser,
} from 'react-icons/fa';
import RefundForm from './RefundForm';
import {
  REFUND_STATUS_DOT,
  formatRefundMoney,
  refundAmountPending,
  refundFormFromRefund,
  refundPayload,
} from '../utils/refunds';
import './Refunds.css';

const formatDate = (value, lang) => (value ? new Date(value).toLocaleString(lang, {
  day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
}) : '-');

const RefundDetail = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'fr';
  const { refundId } = useParams();
  const navigate = useNavigate();
  const [refund, setRefund] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const token = localStorage.getItem('access_token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchRefund = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`/api/logistics/refunds/${refundId}`, { headers });
      setRefund(response.data);
      setForm(refundFormFromRefund(response.data));
    } catch (err) {
      console.error('Failed to fetch refund:', err);
      setError(err?.response?.data?.detail || t('refunds.fetchDetailError', 'Failed to load refund.'));
    } finally {
      setLoading(false);
    }
  }, [headers, refundId, t]);

  useEffect(() => {
    fetchRefund();
  }, [fetchRefund]);

  const handleSave = async (event) => {
    event.preventDefault();
    if (!form) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const response = await axios.patch(`/api/logistics/refunds/${refundId}`, refundPayload(form), { headers });
      setRefund(response.data);
      setForm(refundFormFromRefund(response.data));
      setSuccess(t('refunds.updateSuccess', 'Refund updated successfully.'));
    } catch (err) {
      console.error('Failed to update refund:', err);
      setError(err?.response?.data?.detail || t('refunds.updateError', 'Failed to update refund.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    setError('');
    try {
      await axios.delete(`/api/logistics/refunds/${refundId}`, { headers });
      navigate('/refunds');
    } catch (err) {
      console.error('Failed to delete refund:', err);
      setError(err?.response?.data?.detail || t('refunds.deleteError', 'Failed to delete refund.'));
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="refunds-page">
        <div className="refund-loading large">{t('refunds.loadingDetail', 'Loading refund...')}</div>
      </div>
    );
  }

  if (!refund || !form) {
    return (
      <div className="refunds-page">
        <button type="button" className="refund-back-button" onClick={() => navigate('/refunds')}>
          <FaArrowLeft />
          {t('refunds.backToRefunds', 'Back to refunds')}
        </button>
        <div className="refund-alert error">{error || t('refunds.notFound', 'Refund not found.')}</div>
      </div>
    );
  }

  const byAi = refund.created_by_kind === 'ai';
  const subtitle = [
    refund.order_reference && t('refunds.orderShort', 'Cde {{order}}', { order: refund.order_reference }),
    refund.client_name || refund.merchant_name,
    refund.carrier_name,
  ].filter(Boolean).join(' · ');
  const history = Array.isArray(refund.history) ? refund.history : [];

  return (
    <div className="refunds-page">
      <header className="refunds-header">
        <div className="refunds-header-title">
          <FaMoneyBillWave className="refunds-header-icon" />
          <div>
            <button type="button" className="refund-back-button inline" onClick={() => navigate('/refunds')}>
              <FaArrowLeft />
              {t('refunds.backToRefunds', 'Back to refunds')}
            </button>
            <h1>{t('refunds.detailTitle', 'Refund detail')}</h1>
            <p>{subtitle || refund.external_ticket_id || refund._id}</p>
          </div>
        </div>
        <button
          type="button"
          className="refund-secondary-button"
          onClick={() => navigate(`/tickets/${refund.ticket_id}/messages`)}
        >
          <FaExternalLinkAlt />
          {t('refunds.openTicket', 'Open ticket')}
        </button>
      </header>

      <section className="refund-kpi-grid detail">
        <div className="refund-kpi-card">
          <span>{t('refunds.totalAmount', 'Total amount')}</span>
          <strong>
            {refundAmountPending(refund)
              ? t('refunds.amountToConfirm', 'Amount to confirm')
              : formatRefundMoney(refund.total_refund_amount, refund.currency, lang)}
          </strong>
        </div>
        <div className="refund-kpi-card">
          <span>{t('refunds.status', 'Status')}</span>
          <strong>{t(`refunds.statuses.${refund.status}`, refund.status)}</strong>
        </div>
        <div className="refund-kpi-card">
          <span>{t('refunds.orderReference', 'Order')}</span>
          <strong>{refund.order_reference || '-'}</strong>
        </div>
        <div className="refund-kpi-card">
          <span>{t('refunds.clientName', 'Client')}</span>
          <strong>{refund.client_name || refund.merchant_name || '-'}</strong>
        </div>
        <div className="refund-kpi-card">
          <span>{t('refunds.carrierName', 'Carrier')}</span>
          <strong>{refund.carrier_name || '-'}</strong>
        </div>
      </section>

      <section className="refund-detail-grid">
        <form className="refund-panel refund-form" onSubmit={handleSave}>
          <div className="refund-panel-header">
            <div>
              <h2>{t('refunds.workflow', 'Workflow')}</h2>
              <span>{t('refunds.workflowSubtitle', 'Update the source, reason, status, and amount.')}</span>
            </div>
          </div>

          <RefundForm
            form={form}
            onChange={(field, value) => setForm((prev) => ({ ...prev, [field]: value }))}
            declared={refund.declared}
            ticketOrder={refund.order_reference}
            idPrefix="rfm-detail"
          />

          {error && <div className="refund-alert error">{error}</div>}
          {success && <div className="refund-alert success">{success}</div>}

          <div className="refund-actions">
            <button type="submit" className="refund-primary-button" disabled={saving}>
              <FaSave />
              {saving ? t('refunds.saving', 'Saving...') : t('refunds.saveChanges', 'Save changes')}
            </button>
            <button type="button" className="refund-danger-button" onClick={handleDelete} disabled={deleting}>
              <FaTrash />
              {deleting ? t('refunds.deleting', 'Deleting...') : t('refunds.deleteRefund', 'Delete refund')}
            </button>
          </div>
        </form>

        <aside className="refund-panel refund-metadata-panel">
          <div className="refund-panel-header">
            <div>
              <h2>{t('refunds.provenance', 'Provenance')}</h2>
              <span>{t('refunds.metadataSubtitle', 'Audit and ticket context.')}</span>
            </div>
          </div>
          <div className="rf-provenance">
            <div className="rf-provenance-who">
              {byAi ? <FaRobot aria-hidden="true" /> : <FaUser aria-hidden="true" />}
              {byAi
                ? t('refunds.provenanceAi', 'Recorded by the AI agent from the carrier\'s answer')
                : t('refunds.provenanceHuman', 'Recorded by {{who}}', { who: refund.created_by_email || t('refunds.recordedByTeam', 'Team') })}
            </div>
            {refund.evidence?.text && (
              <>
                <span className="rf-history-label">{t('refunds.evidence', 'The carrier\'s words')}</span>
                <blockquote className="rf-evidence">« {refund.evidence.text} »</blockquote>
              </>
            )}
            {history.length > 0 && (
              <ol className="rf-history" aria-label={t('refunds.history', 'History')}>
                {history.map((entry, index) => (
                  <li key={`${entry.status}-${entry.at}-${index}`}>
                    <span className="rf-history-dot" style={{ background: REFUND_STATUS_DOT[entry.status] || 'var(--blue)' }} aria-hidden="true" />
                    <span>
                      {t(`refunds.statuses.${entry.status}`, entry.status)}
                      {' · '}
                      {entry.by_kind === 'ai' ? t('refunds.recordedByAiOption', 'AI agent') : (entry.by_email || t('refunds.recordedByTeam', 'Team'))}
                    </span>
                    <time dateTime={entry.at}>{formatDate(entry.at, lang)}</time>
                  </li>
                ))}
              </ol>
            )}
          </div>
          <dl className="refund-definition-list">
            <div><dt>{t('refunds.externalTicketId', 'External ticket')}</dt><dd>{refund.external_ticket_id || '-'}</dd></div>
            <div><dt>{t('refunds.trackingNumber', 'Tracking number')}</dt><dd>{refund.tracking_number || '-'}</dd></div>
            <div><dt>{t('refunds.carrierReference', 'Carrier claim ref.')}</dt><dd>{refund.carrier_reference || '-'}</dd></div>
            <div><dt>{t('refunds.settlement', 'Settlement')}</dt><dd>{t(`refunds.settlements.${refund.settlement || 'unknown'}`, refund.settlement || '-')}</dd></div>
            <div><dt>{t('refunds.clientId', 'Client ID')}</dt><dd>{refund.client_id || '-'}</dd></div>
            <div><dt>{t('refunds.createdAt', 'Created')}</dt><dd>{formatDate(refund.created_at, lang)}</dd></div>
            <div><dt>{t('refunds.updatedBy', 'Updated by')}</dt><dd>{refund.updated_by_kind === 'ai' ? t('refunds.recordedByAiOption', 'AI agent') : (refund.updated_by_email || '-')}</dd></div>
            <div><dt>{t('refunds.updatedAt', 'Updated')}</dt><dd>{formatDate(refund.updated_at, lang)}</dd></div>
          </dl>
        </aside>
      </section>
    </div>
  );
};

export default RefundDetail;
